import { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../icons";
import { statusOf } from "../testStatus";
import { useTheme } from "../theme";
import ConfirmModal, { useConfirm } from "./ConfirmModal";
import CreateTestCasePage from "./CreateTestCasePage";
import { DIALOG_HEIGHT } from "./dialogSize";
import HelpButton from "./HelpDialog";
import ManualTestCasePage from "./ManualTestCasePage";
import RunHistory from "./RunHistory";
import RunView, { CredentialsPromptModal, DynamicPromptModal } from "./RunView";
import TestCaseRow from "./TestCaseRow";
import { useRunSockets } from "../hooks/useRunSocket";
import { runControl, runStatePoll, runStepStatus } from "../hooks/runPolls";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function apiWsUrl(path) {
  const base = new URL(API_BASE_URL, window.location.origin);
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}${path}`;
}

/* Turn an API test case into the shape the row + player expects. */
function stepScript(s) {
  const sel = JSON.stringify(s.selector || "");
  const val = s.value != null ? JSON.stringify(String(s.value)) : "";
  switch (s.action) {
    case "fill":
      return `await page.fill(${sel}, ${val});`;
    case "type":
      return `await page.type(${sel}, ${val});`;
    case "click":
      return `await page.click(${sel});`;
    case "press":
      return `await page.press(${sel}, ${val});`;
    case "selectOption":
      return `await page.selectOption(${sel}, { label: ${val} });`;
    case "selectDropdown":
      return `// PrimeReact dropdown: open ${sel} and pick ${val}`;
    case "expectVisible":
      return `await expect(page.locator(${sel})).toBeVisible();`;
    case "expectText":
      return `await expect(page.locator(${sel})).toContainText(${val});`;
    case "expectUrlContains":
      return `await expect(page).toHaveURL(new RegExp(${val}));`;
    case "expectDisabled":
      return `await expect(page.locator(${sel})).toBeDisabled();`;
    case "expectEnabled":
      return `await expect(page.locator(${sel})).toBeEnabled();`;
    case "expectFocused":
      return `await expect(page.locator(${sel})).toBeFocused();`;
    case "expectNotFocused":
      return `await expect(page.locator(${sel})).not.toBeFocused();`;
    case "expectValue":
      return `await expect(page.locator(${sel})).toHaveValue(${val});`;
    default:
      return `// unsupported action: ${s.action}`;
  }
}

function describeStep(s) {
  if (s.description) return s.description;
  const bits = [s.action];
  if (s.selector) bits.push(s.selector);
  if (s.value != null && s.value !== "") bits.push(`= "${s.value}"`);
  return bits.join(" ");
}

function resolveCreatedVia(source) {
  const src = source || {};
  // Explicit field wins (set by both create flows going forward).
  if (src.creation === "ai" || src.creation === "manual") return src.creation;
  // Heuristic fallback for legacy rows saved before `creation` existed. The
  // recorder always writes `url` / `recorded_total`; the AI flow writes at
  // least one of the AI-only keys below.
  if (
    src.category ||
    src.connectTo ||
    src.jiraKey ||
    src.jiraTicket ||
    src.changelogCommit ||
    src.changelogVersion
  ) {
    return "ai";
  }
  if (src.url || src.recorded_total != null) return "manual";
  return null;
}

function fromApi(t) {
  const rawSteps = Array.isArray(t.steps) ? t.steps : [];
  // Per-step outcomes persisted from the last run (if any), so a reloaded
  // case still shows which step passed/failed instead of resetting to grey.
  const lastSteps = Array.isArray(t.last_run?.steps) ? t.last_run.steps : [];
  return {
    id: t.testcasekey,
    testcasekey: t.testcasekey,
    boardId: t.board_id,
    subcategoryKey: t.subcategorykey || null,
    groupKey: t.groupkey || null,
    name: t.name,
    priority: t.priority || "Medium",
    status: t.last_run?.status || t.status || "pending",
    menuLabel: t.menu_label,
    menuPath: t.menu_path || [],
    source: t.source || {},
    aiContext: t.ai_context || "",
    createdBy: t.created_by || null,
    createdVia: resolveCreatedVia(t.source),
    lastRun: t.last_run || null,
    linkedTickets: Array.isArray(t.linked_tickets) ? t.linked_tickets : [],
    /* It ran in a For you run or an execution recorded in Jira: not deleted
       (the API refuses; the reason says which). */
    deleteBlocked: Boolean(t.delete_blocked),
    deleteBlockedReason: t.delete_blocked_reason || "",
    steps: rawSteps.map((s, i) => {
      const prev = lastSteps[i] || null;
      // Screenshots aren't inlined; when one exists we point at the endpoint
      // that streams it from the DB, loaded lazily when the user clicks.
      const shotUrl = prev?.screenshot
        ? `${API_BASE_URL}testcases/${t.testcasekey}/steps/${i + 1}/screenshot` +
          `?v=${encodeURIComponent(t.last_run?.at || "")}`
        : null;
      return {
        name: describeStep(s),
        status: prev?.status || "pending",
        action: s.action,
        selector: s.selector,
        value: s.value,
        // Preserve every field so the inline editor and its save payload
        // can round-trip a step without silently wiping dynamic/screenshot/
        // description metadata that the recorder captured.
        x_ratio: s.x_ratio ?? null,
        y_ratio: s.y_ratio ?? null,
        text: s.text ?? null,
        description: s.description ?? null,
        field_label: s.field_label ?? null,
        kind: s.kind ?? null,
        masked: !!s.masked,
        is_dynamic: !!s.is_dynamic,
        param_name: s.param_name ?? null,
        capture_screenshot: !!s.capture_screenshot,
        constraints: s.constraints ?? null,
        // Xray import: which Xray manual step this Playwright step implements.
        source_step_no: s.source_step_no ?? null,
        script: stepScript(s),
        screenshot: shotUrl,
        error: prev?.error || null,
      };
    }),
  };
}

/* ------------------------------------------------------------------ */
/*  Generic modal shell                                                */
/* ------------------------------------------------------------------ */
function Modal({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex w-full max-w-lg flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-none justify-end gap-2 border-t border-slate-100 px-5 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Confirmation dialog (destructive actions)                          */
/* ------------------------------------------------------------------ */

function PropertiesModal({ testCase, onClose }) {
  const s = statusOf(testCase.status);

  // Which Jira work item(s) this test case belongs to. A case may be authored
  // against a Jira Task (the work item) and/or imported from an Xray Test.
  const src = testCase.source || {};
  const linked = testCase.linkedTickets || [];
  // Xray Test key — set by the Xray import flows (manual & AI).
  const xrayItem = src.xray_test_key || src.xrayTestKey || src.xray_key || null;
  // Jira Story — the ticket the case was created for. Older cases were created
  // against a Task, so that type is still accepted as a fallback.
  const taskItem =
    src.jiraTicket ||
    src.jiraKey ||
    (linked.find((t) => /story/i.test(t.issue_type || ""))?.ticket_key) ||
    (linked.find((t) => /task/i.test(t.issue_type || ""))?.ticket_key) ||
    (linked.find((t) => t.ticket_key && t.ticket_key !== xrayItem)?.ticket_key) ||
    null;

  return (
    <Modal title="Test case properties" onClose={onClose}>
      <dl className="space-y-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-slate-500">Name</dt>
          <dd className="font-medium text-slate-900">{testCase.name}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">Priority</dt>
          <dd className="font-medium text-slate-900">{testCase.priority}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">Last status</dt>
          <dd className={["font-medium", s.text].join(" ")}>{s.label}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">Steps</dt>
          <dd className="font-medium text-slate-900">{testCase.steps.length}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">Menu path</dt>
          <dd className="text-right font-medium text-slate-900">
            {(testCase.menuPath || []).join(" > ") || "—"}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">Jira Story</dt>
          <dd className={taskItem ? "font-mono font-medium text-slate-900" : "text-slate-400"}>
            {taskItem || "—"}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">Jira Test Item</dt>
          <dd className={xrayItem ? "font-mono font-medium text-slate-900" : "text-slate-400"}>
            {xrayItem || "—"}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">Created by</dt>
          <dd className="font-medium text-slate-900">{testCase.createdBy || "—"}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">ID</dt>
          <dd className="font-mono text-[11px] text-slate-500">{testCase.id}</dd>
        </div>
      </dl>
    </Modal>
  );
}

/* Modal to create a new sub-category within the board. */
function AddSubcategoryModal({ onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit(name, description);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add category"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {saving && <Icons.Spinner className="h-4 w-4 animate-spin" />}
            Create
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
            Name
          </label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Smoke tests"
            className="w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
            Description <span className="font-normal normal-case text-slate-400">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
          />
        </div>
        {error && <p className="text-xs text-rose-600">{error}</p>}
      </form>
    </Modal>
  );
}

/* Modal to name a group — used both to create a group from the current
   multi-selection and to rename an existing group. */
function GroupNameModal({ title, initialName = "", submitLabel = "Create", onClose, onSubmit }) {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit(name.trim());
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {saving && <Icons.Spinner className="h-4 w-4 animate-spin" />}
            {submitLabel}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
            Sub-category name
          </label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Login flow tests"
            className="w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
          />
        </div>
        {error && <p className="text-xs text-rose-600">{error}</p>}
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  Board detail                                                       */
/* ------------------------------------------------------------------ */
export default function BoardDetail({
  board,
  onBack,
  initialSubKey = null,
  initialViewCaseId = null,
  onActiveSubChange,
  onSubcategoriesChanged,
  onDeleteBoard,
}) {
  const [ask, confirmDialog] = useConfirm();
  const { userkey } = useTheme();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [running, setRunning] = useState(() => new Set());
  // testCaseIds that the client has paused via the Pause button.
  const [pausedCases, setPausedCases] = useState(() => new Set());
  const [groupRunning, setGroupRunning] = useState(() => new Set()); // groupkeys with a "Run all" in progress
  const [createMode, setCreateMode] = useState(null); // null | "ai" | "manual"
  const [createTarget, setCreateTarget] = useState(null); // subcategorykey to create under, or null (standalone)
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef(null);
  const [subcategories, setSubcategories] = useState([]);
  const [groups, setGroups] = useState([]); // [{ groupkey, name, subcategorykey, total, ... }]
  const [expandedGroups, setExpandedGroups] = useState(() => new Set()); // groupkeys open in the accordion
  const [selectMode, setSelectMode] = useState(false); // multi-select for grouping
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // selected testcasekeys
  const [groupModal, setGroupModal] = useState(null); // { mode:"create"|"rename", groupkey?, name? }
  const [activeSub, setActiveSub] = useState(initialSubKey); // subcategorykey drilled into, or null (board level)
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [propsFor, setPropsFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null); // testCase whose run history is shown
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name } of case pending delete confirmation
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [groupDeleteTarget, setGroupDeleteTarget] = useState(null); // { groupkey, name } pending delete confirmation
  const [groupDeleteBusy, setGroupDeleteBusy] = useState(false);
  const [caseQuery, setCaseQuery] = useState(""); // filters the test-case list
  const [ticketFilter, setTicketFilter] = useState(""); // filter by linked Jira ticket key
  const [runFrames, setRunFrames] = useState({}); // { [testCaseId]: data_url }
  const [viewCaseId, setViewCaseId] = useState(initialViewCaseId || null); // testCaseId shown in the full-page run view
  const [toast, setToast] = useState(null); // { message, kind: "success" | "error" }
  // Pending dynamic-value prompt per case, if any. Populated by the runner's
  // `prompt_input` event; cleared when the user submits a value.
  //   { [testCaseId]: { index, param_name, field_label?, kind?, masked, action, selector } }
  const [pendingPrompts, setPendingPrompts] = useState({});
  // { [testCaseId]: true } — a run of an auto-login case is waiting for the
  // user to type credentials. Cleared once the credentials are submitted.
  const [pendingCredentials, setPendingCredentials] = useState({});
  // Datasources grouped by testcasekey — powers the Run split-button dropdown.
  //   { [testCaseId]: [{datasourcekey, name, ...}] }
  const [datasourcesByCase, setDatasourcesByCase] = useState({});
  const wsRefs = useRef({}); // { [testCaseId]: run stream handle (useRunSockets) }
  const openSocket = useRunSockets();
  // Each running case's stream state ({id: "reconnecting" | "polling" | "lost"}).
  const [connections, setConnections] = useState({});
  // Per-run flag: set to true when an `errors_detected` event arrives, so
  // the terminal `done` toast can say "app rejected input" instead of the
  // generic "failed".
  const runAppRejected = useRef({}); // { [testCaseId]: boolean }

  const showToast = useCallback((message, kind = "success") => {
    setToast({ message, kind });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  // Close the "create test case" split-button menu on outside click / Escape.
  useEffect(() => {
    if (!createMenuOpen) return undefined;
    const onClick = (e) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target)) {
        setCreateMenuOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setCreateMenuOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [createMenuOpen]);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [casesRes, subsRes, dsRes] = await Promise.all([
        fetch(`${API_BASE_URL}testcases?board_id=${encodeURIComponent(board.id)}`),
        fetch(`${API_BASE_URL}boards/${encodeURIComponent(board.id)}/subcategories`),
        fetch(`${API_BASE_URL}datasources`),
      ]);
      const body = await casesRes.json().catch(() => ({}));
      if (!casesRes.ok) throw new Error(body?.detail || `HTTP ${casesRes.status}`);
      const list = Array.isArray(body?.data) ? body.data.map(fromApi) : [];
      setCases(list);
      // Sub-categories are non-critical: on failure just show none.
      const subsBody = await subsRes.json().catch(() => ({}));
      setSubcategories(subsRes.ok && Array.isArray(subsBody?.data) ? subsBody.data : []);
      // Groups are non-critical too: fetch separately so a failure here can't
      // break the main list.
      try {
        const grpRes = await fetch(
          `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups`
        );
        const grpBody = await grpRes.json().catch(() => ({}));
        setGroups(grpRes.ok && Array.isArray(grpBody?.data) ? grpBody.data : []);
      } catch {
        setGroups([]);
      }
      // Datasources — group by testcasekey for the split-button picker.
      const dsBody = await dsRes.json().catch(() => ({}));
      if (dsRes.ok && Array.isArray(dsBody?.data)) {
        const grouped = {};
        for (const d of dsBody.data) {
          const k = d.testcasekey;
          if (!grouped[k]) grouped[k] = [];
          grouped[k].push(d);
        }
        setDatasourcesByCase(grouped);
      } else {
        setDatasourcesByCase({});
      }
    } catch (e) {
      setLoadError(e.message);
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [board.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Follow sidebar drill-in: when the parent asks for a specific sub-category
  // (or the board root), switch to it.
  useEffect(() => {
    setActiveSub(initialSubKey || null);
  }, [initialSubKey]);

  // Keep the sidebar highlight in sync when the user drills via KPI cards.
  useEffect(() => {
    onActiveSubChange?.(activeSub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSub]);

  // Close any open sockets on unmount.
  useEffect(() => {
    const store = wsRefs.current;
    return () => {
      Object.values(store).forEach((ws) => {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      });
    };
  }, []);

  const patchCase = (id, patch) =>
    setCases((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const patchStep = (id, idx, patch) =>
    setCases((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, steps: c.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }
          : c
      )
    );

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleGroupExpand = (groupkey) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupkey)) next.delete(groupkey);
      else next.add(groupkey);
      return next;
    });

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  // Create a group from the current multi-selection. The group is scoped to
  // the current context: inside a sub-category when one is open, otherwise
  // directly under the board.
  const createGroupFromSelection = async (name) => {
    const ids = [...selectedIds];
    const res = await fetch(
      `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          subcategorykey: activeSub || null,
          userkey: userkey || null,
          testcasekeys: ids,
        }),
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    const body = await res.json().catch(() => ({}));
    // Auto-expand the freshly created group so its members are visible.
    const newKey = body?.data?.groupkey;
    if (newKey) setExpandedGroups((prev) => new Set(prev).add(newKey));
    setGroupModal(null);
    exitSelectMode();
    await reload();
    showToast(`Filed ${ids.length} test case${ids.length === 1 ? "" : "s"} under "${name}"`);
  };

  // Remove the current selection from whatever group(s) they're in.
  const ungroupSelection = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups/assign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupkey: null, testcasekeys: ids }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      exitSelectMode();
      await reload();
      showToast(`Removed ${ids.length} test case${ids.length === 1 ? "" : "s"} from their sub-category`);
    } catch (e) {
      showToast(`Could not update the sub-category: ${e.message}`, "error");
    }
  };

  // Remove a single test case from its group (leaves the group intact).
  const ungroupCase = async (id) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups/assign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupkey: null, testcasekeys: [id] }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reload();
      showToast("Removed from sub-category");
    } catch (e) {
      showToast(`Could not update the sub-category: ${e.message}`, "error");
    }
  };

  // Dissolve an entire group: the group is removed and all its cases become
  // ungrouped (kept). Backed by DELETE + ON DELETE SET NULL on testcases.
  const ungroupWholeGroup = async (groupkey, count) => {
    const ok = await ask({
      title: "Dissolve sub-category",
      message: `Dissolve this sub-category? Its ${count} test case${count === 1 ? "" : "s"} are kept and moved out of it.`,
      confirmLabel: "Dissolve",
    });
    if (!ok) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups/${groupkey}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reload();
      showToast("Sub-category dissolved");
    } catch (e) {
      showToast(`Could not update the sub-category: ${e.message}`, "error");
    }
  };

  // Delete an empty group from the DB. Only offered when the group has no
  // member test cases (the UI disables the action otherwise), so nothing is
  // lost. Routed through the shared ConfirmModal for confirmation.
  const confirmDeleteGroup = async () => {
    if (!groupDeleteTarget || groupDeleteBusy) return;
    setGroupDeleteBusy(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups/${groupDeleteTarget.groupkey}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setGroupDeleteTarget(null);
      await reload();
      showToast("Sub-category deleted");
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, "error");
    } finally {
      setGroupDeleteBusy(false);
    }
  };

  const renameGroup = async (groupkey, name) => {
    const res = await fetch(
      `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups/${groupkey}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    setGroupModal(null);
    await reload();
  };

  // Kicks off a run and resolves when it finishes (WS closes) so callers like
  // "Run all" can execute a group sequentially.
  const runCase = (id, datasourcekey = null) =>
    new Promise((resolve) => {
      const tc = cases.find((c) => c.id === id);
      if (!tc) return resolve();
      // Reset per-step state and mark case as running.
      setCases((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                status: "running",
                steps: c.steps.map((s) => ({ ...s, status: "pending", screenshot: null, error: null })),
              }
            : c
        )
      );
      setExpanded((prev) => new Set(prev).add(id));
      setRunning((prev) => new Set(prev).add(id));
      // Fresh run — clear the app-rejected flag from a prior run so the
      // terminal toast reflects only this run's events.
      runAppRejected.current[id] = false;
      setConnections((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });

      (async () => {
        let runId = null;
        try {
          const res = await fetch(`${API_BASE_URL}testcases/${id}/run`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userkey: userkey || null,
              datasourcekey: datasourcekey || null,
            }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok || !body?.data?.run_id) {
            throw new Error(body?.detail || `HTTP ${res.status}`);
          }
          runId = body.data.run_id;
        } catch (e) {
          patchCase(id, { status: "failed" });
          setRunning((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          return resolve();
        }

        /* A drop reconnects, then follows the run over HTTP; only the run's
           done event (live or read over HTTP) ends it. */
        const handle = openSocket(apiWsUrl(`/api/ws/run/${runId}`), {
          onEvent: (msg) => handleRunEvent(id, msg),
          onStatus: (s) => setConnections((p) => ({ ...p, [id]: s })),
          poll: runStatePoll(runId),
          sendFallback: runControl(runId),
          onEnd: ({ reason }) => {
            if (wsRefs.current[id] === handle) delete wsRefs.current[id];
            setConnections((p) => {
              const n = { ...p };
              if (reason === "lost") n[id] = "lost";
              else delete n[id];
              return n;
            });
            setRunning((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
            resolve();
          },
        });
        wsRefs.current[id] = handle;
      })();
    });

  // Ask the server to stop an in-progress run. The backend unwinds at the next
  // checkpoint, persists a "cancelled" result, and closes the WS (which clears
  // the running flag via runCase's onclose handler).
  const stopCase = (id) => {
    // Over the socket, or over HTTP while it is down (runControl).
    if (!wsRefs.current[id]?.send({ type: "cancel" })) return;
    // Clear paused UI immediately so buttons flip out of "Continue" state
    // while the backend unwinds.
    setPausedCases((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    // Flip case status + immediately clear any lingering "running" step
    // marker. The backend won't emit a step_result for the step that was
    // mid-execution when the cancel arrived (it just breaks the loop), so
    // without this the orange "Running" pill would stay on that step
    // forever until the next run.
    setCases((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              status: "cancelled",
              steps: c.steps.map((s) =>
                s.status === "running" ? { ...s, status: "pending" } : s
              ),
            }
          : c
      )
    );
  };

  const pauseCase = (id) => {
    if (!wsRefs.current[id]?.send({ type: "pause" })) return;
    // Optimistically flip the UI — backend confirms with `paused` event.
    setPausedCases((prev) => new Set(prev).add(id));
  };

  const resumeCase = (id) => {
    if (!wsRefs.current[id]?.send({ type: "resume" })) return;
    setPausedCases((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // Run every case in a group, one after another.
  const runGroup = async (groupkey, memberIds) => {
    if (!memberIds.length || groupRunning.has(groupkey)) return;
    setGroupRunning((prev) => new Set(prev).add(groupkey));
    try {
      for (const id of memberIds) {
        await runCase(id);
      }
    } finally {
      setGroupRunning((prev) => {
        const next = new Set(prev);
        next.delete(groupkey);
        return next;
      });
    }
  };

  function handleRunEvent(id, evt) {
    if (!evt || !evt.type) return;
    switch (evt.type) {
      case "frame":
        if (evt.data_url) {
          setRunFrames((prev) => ({ ...prev, [id]: evt.data_url }));
        }
        break;
      case "step_start":
        patchStep(id, evt.index - 1, {
          status: "running",
          script: evt.script || undefined,
          action: evt.action,
          selector: evt.selector,
          value: evt.value,
          resolvedSource: evt.resolved_source || null,
          resolvedMasked: !!evt.resolved_masked,
          error: null,
        });
        break;
      case "prompt_input":
        patchStep(id, evt.index - 1, { status: "running" });
        setPendingPrompts((prev) => ({
          ...prev,
          [id]: {
            index: evt.index,
            param_name: evt.param_name,
            field_label: evt.field_label,
            kind: evt.kind,
            masked: !!evt.masked,
            action: evt.action,
            selector: evt.selector,
          },
        }));
        break;
      case "prompt_credentials":
        setPendingCredentials((prev) => ({ ...prev, [id]: true }));
        break;
      case "run_state":
        // Read over HTTP after a drop: each step as recorded (status and
        // error only; a screenshot it already had stays).
        (evt.steps || []).forEach((s) =>
          patchStep(id, s.index - 1, { status: runStepStatus(s.status), error: s.error || null })
        );
        break;
      case "step_result":
        patchStep(id, evt.index - 1, {
          status: evt.status === "pass" ? "passed" : "failed",
          screenshot: evt.screenshot || null,
          error: evt.error || null,
        });
        break;
      case "paused":
        setPausedCases((prev) => new Set(prev).add(id));
        break;
      case "resumed":
        setPausedCases((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        break;
      case "errors_detected":
        // The runner scanned the DOM after a primary-action click (Save /
        // Sign / etc.) and found visible validation errors. Attach them
        // to the step so the RunView can render "App rejected — N errors
        // visible" underneath it. Flag the run so the terminal toast
        // reads "app rejected input" instead of the generic "failed".
        runAppRejected.current[id] = true;
        patchStep(id, evt.step_index - 1, {
          detectedErrors: Array.isArray(evt.errors) ? evt.errors : [],
        });
        break;
      case "done": {
        const cancelled = evt.status === "cancelled";
        const passed = evt.status === "passed";
        patchCase(id, { status: cancelled ? "cancelled" : passed ? "passed" : "failed" });
        // Leave the run view open so the user can review each step and its
        // captured screenshot; just drop the transient live frame.
        setRunFrames((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setPendingPrompts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setPendingCredentials((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        const tcName = cases.find((c) => c.id === id)?.name || "Test case";
        const appRejected = !!runAppRejected.current[id];
        runAppRejected.current[id] = false;
        showToast(
          cancelled
            ? `"${tcName}" stopped`
            : passed
            ? `Test case "${tcName}" has run successfully.`
            : appRejected
            ? `Test case "${tcName}" failed — app rejected input.`
            : `Test case "${tcName}" failed.`,
          cancelled ? "info" : passed ? "success" : "error"
        );
        break;
      }
      case "error": {
        setCases((prev) =>
          prev.map((c) => (c.id === id ? { ...c, status: "failed" } : c))
        );
        setRunFrames((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setPendingPrompts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setPendingCredentials((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        const tcName = cases.find((c) => c.id === id)?.name || "Test case";
        const appRejected = !!runAppRejected.current[id];
        runAppRejected.current[id] = false;
        showToast(
          appRejected
            ? `Test case "${tcName}" failed — app rejected input.`
            : `Test case "${tcName}" failed.`,
          "error"
        );
        break;
      }
      default:
        break;
    }
  }

  const submitPromptInput = useCallback((id, index, value) => {
    const ws = wsRefs.current[id];
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "input_value", index, value }));
    } catch {
      /* ignore */
    }
    setPendingPrompts((prev) => {
      const cur = prev[id];
      if (!cur || cur.index !== index) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const submitCredentials = useCallback((id, username, password) => {
    const ws = wsRefs.current[id];
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "credentials_value", username, password }));
    } catch {
      /* ignore */
    }
    setPendingCredentials((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const openView = (tc) => {
    // Open the dedicated full-page run view (video + steps), whether or not a
    // run is currently in progress.
    setViewCaseId(tc.id);
  };

  const saveEdits = async (id, name, steps) => {
    try {
      /* Who edited it, for the Artifact's activity log. */
      const res = await fetch(`${API_BASE_URL}testcases/${id}${userkey ? `?userkey=${encodeURIComponent(userkey)}` : ""}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          // Ship every field the API's TestCaseStep schema recognises. The
          // previous slim payload (action/selector/value only) silently wiped
          // is_dynamic, param_name, capture_screenshot and description on
          // every edit — turning parameterised tests back into plain fills.
          steps: steps.map((s) => ({
            action: s.action,
            selector: s.selector || null,
            value: s.value ?? null,
            x_ratio: s.x_ratio ?? null,
            y_ratio: s.y_ratio ?? null,
            text: s.text ?? null,
            description: s.description ?? null,
            field_label: s.field_label ?? null,
            kind: s.kind ?? null,
            masked: !!s.masked,
            is_dynamic: !!s.is_dynamic,
            param_name: s.is_dynamic ? s.param_name || null : null,
            capture_screenshot: !!s.capture_screenshot,
            constraints: s.constraints ?? null,
            // Xray mapping: which Xray manual step this step implements.
            // Must round-trip or the Xray → Playwright grid loses its mapping.
            source_step_no: s.source_step_no ?? null,
          })),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      await reload();
      showToast("Edited steps saved successfully");
    } catch (e) {
      showToast(`Save failed: ${e.message}`, "error");
    }
  };

  // Open the confirmation dialog for a test case delete.
  const requestDeleteCase = (id) => {
    const tc = cases.find((c) => c.id === id);
    setDeleteTarget({ id, name: tc?.name || "" });
  };

  // Perform the delete once the user confirms in the dialog.
  const confirmDeleteCase = async () => {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}testcases/${deleteTarget.id}${userkey ? `?userkey=${encodeURIComponent(userkey)}` : ""}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        /* A refusal says why (e.g. it ran in a For you run): shown as is. */
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
      }
      setCases((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setDeleteTarget(null);
      showToast(`Delete failed: ${e.message}`, "error");
    } finally {
      setDeleteBusy(false);
    }
  };

  // Begin creating a test case, optionally under a given sub-category
  // (subKey = null => standalone under the board).
  const startCreate = (mode, subKey = null) => {
    setCreateTarget(subKey);
    setCreateMode(mode);
    setCreateMenuOpen(false);
  };

  const deleteThisBoard = async () => {
    const ok = await ask({
      title: "Delete test suite",
      message: `Delete test suite "${board.name}" and all its test cases, categories and run history? This cannot be undone.`,
    });
    if (ok) onDeleteBoard?.(board.id);
  };

  const addSubcategory = async (name, description) => {
    const res = await fetch(
      `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/subcategories`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          description: (description || "").trim(),
          userkey: userkey || null,
        }),
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    setAddSubOpen(false);
    await reload();
    onSubcategoriesChanged?.();
  };

  const deleteSubcategory = async (subKey) => {
    const ok = await ask({
      title: "Delete category",
      message: "Delete this category? Its test cases are kept and moved out to the test suite (standalone).",
    });
    if (!ok) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/subcategories/${subKey}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (activeSub === subKey) setActiveSub(null);
      await reload();
      onSubcategoriesChanged?.();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  // Render the full-page create flow in place of the board table.
  if (createMode === "ai") {
    return (
      <CreateTestCasePage
        board={board}
        subcategorykey={createTarget}
        onCancel={() => setCreateMode(null)}
        onCreate={() => {
          setCreateMode(null);
          setCreateTarget(null);
          reload();
        }}
      />
    );
  }
  if (createMode === "manual") {
    return (
      <ManualTestCasePage
        board={board}
        subcategorykey={createTarget}
        onCancel={() => setCreateMode(null)}
        onCreate={() => {
          setCreateMode(null);
          setCreateTarget(null);
          reload();
        }}
      />
    );
  }

  // Full-page run view (video + steps) shown in place of the board table.
  if (viewCaseId) {
    const viewCase = cases.find((c) => c.id === viewCaseId);
    if (viewCase) {
      return (
        <>
          <RunView
            testCase={viewCase}
            frameUrl={runFrames[viewCaseId]}
            isRunning={running.has(viewCaseId)}
            connection={connections[viewCaseId]}
            isPaused={pausedCases.has(viewCaseId)}
            onBack={() => setViewCaseId(null)}
            onRun={runCase}
            onStop={(id) => {
              // Stop from the run view kicks the user back to the board so
              // they don't stare at a frozen 'Stopped' screen.
              stopCase(id);
              setViewCaseId(null);
            }}
            onPause={() => pauseCase(viewCaseId)}
            onResume={() => resumeCase(viewCaseId)}
            onHistory={() => setHistoryFor(viewCase)}
            pendingPrompt={pendingPrompts[viewCaseId] || null}
            onSubmitPrompt={(value) =>
              submitPromptInput(viewCaseId, pendingPrompts[viewCaseId]?.index, value)
            }
            pendingCredentials={!!pendingCredentials[viewCaseId]}
            onSubmitCredentials={(u, p) => submitCredentials(viewCaseId, u, p)}
          />
          {historyFor && (
            <RunHistory testCase={historyFor} onClose={() => setHistoryFor(null)} />
          )}
        </>
      );
    }
  }

  const cq = caseQuery.trim().toLowerCase();
  const filteredCases = cases.filter((c) => {
    if (
      cq &&
      !(
        c.name.toLowerCase().includes(cq) ||
        (c.menuLabel || "").toLowerCase().includes(cq) ||
        (c.priority || "").toLowerCase().includes(cq)
      )
    ) {
      return false;
    }
    if (
      ticketFilter &&
      !(c.linkedTickets || []).some((t) => t.ticket_key === ticketFilter)
    ) {
      return false;
    }
    return true;
  });

  // Distinct Jira tickets linked to any case in this board — feeds the filter.
  const ticketOptions = Array.from(
    new Map(
      cases
        .flatMap((c) => c.linkedTickets || [])
        .map((t) => [t.ticket_key, t])
    ).values()
  ).sort((a, b) => a.ticket_key.localeCompare(b.ticket_key));

  const standaloneCases = filteredCases.filter((c) => !c.subcategoryKey);
  const casesForSub = (subKey) => filteredCases.filter((c) => c.subcategoryKey === subKey);

  const activeSubObj = activeSub
    ? subcategories.find((s) => s.subcategorykey === activeSub) || null
    : null;
  const subCases = activeSub ? casesForSub(activeSub) : [];

  // Per-sub-category status counts (from the unfiltered set) for the KPI cards.
  const subStats = (subKey) => {
    const list = cases.filter((c) => c.subcategoryKey === subKey);
    const passed = list.filter((c) => c.status === "passed").length;
    const failed = list.filter((c) => c.status === "failed").length;
    return { total: list.length, passed, failed, pending: list.length - passed - failed };
  };

  // Shared renderer for a list of test-case rows (used by every group).
  const renderRows = (list) => (
    <div className="divide-y divide-slate-200">
      {list.map((tc) => (
        <TestCaseRow
          key={tc.id}
          testCase={tc}
          isExpanded={expanded.has(tc.id)}
          isRunning={running.has(tc.id)}
          connection={connections[tc.id]}
          onToggleExpand={toggleExpand}
          onRun={runCase}
          onStop={stopCase}
          onView={openView}
          onProperties={setPropsFor}
          onSaveEdits={saveEdits}
          onDelete={requestDeleteCase}
          onHistory={setHistoryFor}
          selectable={selectMode}
          selected={selectedIds.has(tc.id)}
          onToggleSelect={toggleSelect}
          onUngroup={ungroupCase}
          datasources={datasourcesByCase[tc.id] || []}
        />
      ))}
    </div>
  );

  // Renders a context's cases (either the test suite's standalone cases or one
  // sub-category's cases) as group accordions followed by ungrouped rows.
  // `subKey` is the context: null => board level, otherwise the sub-category.
  const renderGroupedSection = (contextCases, subKey) => {
    const groupsHere = groups.filter((g) => (g.subcategorykey || null) === (subKey || null));
    const ungrouped = contextCases.filter((c) => !c.groupKey);
    // Show a group when it has visible members, OR when it is genuinely empty
    // (no members at all) so the user can delete the leftover group. A group
    // whose members are merely hidden by the search filter stays hidden.
    const visibleGroups = groupsHere
      .map((g) => ({
        group: g,
        members: contextCases.filter((c) => c.groupKey === g.groupkey),
        // Unfiltered member count — the real "is this group empty?" signal.
        totalMembers: cases.filter((c) => c.groupKey === g.groupkey).length,
      }))
      .filter((x) => x.totalMembers === 0 || x.members.length > 0);

    if (visibleGroups.length === 0 && ungrouped.length === 0) return null;

    return (
      <div className="space-y-3">
        {visibleGroups.map(({ group, members, totalMembers }) => {
          const open = expandedGroups.has(group.groupkey);
          const isEmpty = totalMembers === 0;
          return (
            <div
              key={group.groupkey}
              className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm"
            >
              <div className="flex items-center gap-2 bg-slate-50 px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => toggleGroupExpand(group.groupkey)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  aria-expanded={open}
                >
                  <Icons.ChevronRight
                    className={[
                      "h-4 w-4 flex-none text-slate-400 transition-transform",
                      open ? "rotate-90" : "",
                    ].join(" ")}
                  />
                  <Icons.Boards className="h-4 w-4 flex-none text-brand-600" />
                  <span className="truncate font-semibold text-slate-900" title={group.name}>
                    {group.name}
                  </span>
                  <span className="flex-none rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                    {members.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => runGroup(group.groupkey, members.map((m) => m.id))}
                  disabled={groupRunning.has(group.groupkey) || members.length === 0}
                  title="Run every test case in this sub-category"
                  className="inline-flex flex-none items-center gap-1.5 rounded-md bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {groupRunning.has(group.groupkey) ? (
                    <>
                      <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                      Running…
                    </>
                  ) : (
                    <>
                      <Icons.Play className="h-3.5 w-3.5" />
                      Run all
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setGroupModal({ mode: "rename", groupkey: group.groupkey, name: group.name })
                  }
                  aria-label="Rename sub-category"
                  title="Rename sub-category"
                  className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                >
                  <Icons.Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => ungroupWholeGroup(group.groupkey, members.length)}
                  disabled={isEmpty}
                  title="Dissolve (keeps test cases)"
                  className="flex-none rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-200 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  Dissolve
                </button>
                <button
                  type="button"
                  onClick={() => setGroupDeleteTarget({ groupkey: group.groupkey, name: group.name })}
                  disabled={!isEmpty}
                  aria-label="Delete sub-category"
                  title={
                    isEmpty
                      ? "Delete this empty sub-category"
                      : "Remove all test cases from the sub-category before deleting it"
                  }
                  className="flex-none rounded-md p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <Icons.Trash className="h-4 w-4" />
                </button>
              </div>
              {open && (
                <div className="border-t border-slate-100">
                  {members.length > 0 ? (
                    renderRows(members)
                  ) : (
                    <p className="px-4 py-3 text-sm text-slate-400">
                      This sub-category is empty — use the trash icon to delete it.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {ungrouped.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
            {renderRows(ungrouped)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        {/* Top row: breadcrumb left, help right. The action cluster below is
            already six buttons wide and wraps, so help lives up here where it
            stays put and does not displace the "Create test case" CTA. */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <Icons.ArrowLeft className="h-4 w-4" />
            Test Suites
          </button>
          <HelpButton screen="board-detail" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            {activeSubObj ? (
              <>
                <div className="mb-0.5 flex items-center gap-1.5 text-sm text-slate-500">
                  <button
                    type="button"
                    onClick={() => setActiveSub(null)}
                    className="font-medium hover:text-slate-800"
                  >
                    {board.name}
                  </button>
                  <Icons.ChevronRight className="h-3.5 w-3.5 flex-none text-slate-400" />
                  <span className="truncate text-slate-700" title={activeSubObj.name}>
                    {activeSubObj.name}
                  </span>
                </div>
                <h1 className="truncate text-xl font-semibold text-slate-900">{activeSubObj.name}</h1>
                <p className="mt-1 text-sm text-slate-500">
                  {subCases.length} test case{subCases.length === 1 ? "" : "s"}
                  {activeSubObj.description ? ` · ${activeSubObj.description}` : ""}
                </p>
              </>
            ) : (
              <>
                <h1 className="text-xl font-semibold text-slate-900">{board.name}</h1>
                <p className="mt-1 text-sm text-slate-500">
                  {cases.length} test case{cases.length === 1 ? "" : "s"}
                  {subcategories.length > 0
                    ? ` · ${subcategories.length} categor${subcategories.length === 1 ? "y" : "ies"}`
                    : ""}
                  {board.description ? ` · ${board.description}` : ""}
                </p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Goes up one level, from inside a category to the category list —
                not to the test suites list, which is what the breadcrumb at the
                very top does. Labelled for the destination to keep the two
                apart. */}
            {activeSubObj && (
              <button
                type="button"
                onClick={() => setActiveSub(null)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Icons.ArrowLeft className="h-4 w-4" />
                Categories
              </button>
            )}
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Icons.Refresh className={["h-4 w-4", loading ? "animate-spin" : ""].join(" ")} />
              Refresh
            </button>
            {cases.length > 0 && (
              <button
                type="button"
                onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                className={[
                  "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition",
                  selectMode
                    ? "border-brand-600 bg-brand-50 text-brand-700"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50",
                ].join(" ")}
              >
                <Icons.Check className="h-4 w-4" />
                {selectMode ? "Done selecting" : "Select to organise"}
              </button>
            )}
            {!activeSubObj && (
              <button
                type="button"
                onClick={() => setAddSubOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Icons.Plus className="h-4 w-4" />
                Add category
              </button>
            )}
            {!activeSubObj && onDeleteBoard && (
              <button
                type="button"
                onClick={deleteThisBoard}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50"
              >
                <Icons.Trash className="h-4 w-4" />
                Delete test suite
              </button>
            )}
            <div className="relative inline-flex" ref={createMenuRef}>
              <button
                type="button"
                onClick={() => setCreateMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={createMenuOpen}
                className="inline-flex items-center gap-2 rounded-l-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800"
              >
                <Icons.Plus className="h-4 w-4" />
                Create test case
              </button>
              <button
                type="button"
                onClick={() => setCreateMenuOpen((o) => !o)}
                aria-label="Choose creation mode"
                aria-haspopup="menu"
                aria-expanded={createMenuOpen}
                className="inline-flex items-center justify-center rounded-r-lg border-l border-brand-800/40 bg-brand-700 px-2 py-2 text-white shadow-sm transition hover:bg-brand-800"
              >
                <Icons.ChevronDown
                  className={[
                    "h-4 w-4 transition-transform",
                    createMenuOpen ? "rotate-180" : "",
                  ].join(" ")}
                />
              </button>
              {createMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => startCreate("ai", activeSub)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <Icons.Plus className="h-4 w-4 text-slate-500" />
                    via AI
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => startCreate("manual", activeSub)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <Icons.Plus className="h-4 w-4 text-slate-500" />
                    via Manual
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {loadError}
        </div>
      )}

      {/* Search — filters the test cases within this board. */}
      {cases.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={caseQuery}
              onChange={(e) => setCaseQuery(e.target.value)}
              placeholder="Search test cases by name, page or priority..."
              aria-label="Search test cases"
              className="w-full rounded-lg border border-slate-300 bg-card py-2 pl-9 pr-9 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
            />
            {caseQuery && (
              <button
                type="button"
                onClick={() => setCaseQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <Icons.X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filter by linked Jira ticket. */}
          {ticketOptions.length > 0 && (
            <div className="relative">
              <select
                value={ticketFilter}
                onChange={(e) => setTicketFilter(e.target.value)}
                aria-label="Filter by ticket"
                className="rounded-lg border border-slate-300 bg-card py-2 pl-3 pr-8 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
              >
                <option value="">All tickets</option>
                {ticketOptions.map((t) => (
                  <option key={t.ticket_key} value={t.ticket_key}>
                    {t.ticket_key}
                    {t.summary ? ` — ${t.summary}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Multi-select action bar — visible while grouping. */}
      {selectMode && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2.5">
          <span className="text-sm font-medium text-brand-800">
            {selectedIds.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => setGroupModal({ mode: "create" })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              <Icons.Plus className="h-4 w-4" />
              Add to a sub-category
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={ungroupSelection}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-card px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Remove from sub-category
            </button>
            <button
              type="button"
              onClick={exitSelectMode}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && cases.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
          <Icons.Spinner className="mx-auto mb-2 h-5 w-5 animate-spin" />
          Loading test cases…
        </div>
      ) : activeSub ? (
        // Inside a sub-category — just its test cases.
        subCases.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
            {caseQuery ? (
              <>
                No test cases match{" "}
                <span className="font-medium text-slate-700">"{caseQuery}"</span>.
              </>
            ) : (
              "No test cases in this category yet. Use “Create test case” to add one."
            )}
          </div>
        ) : (
          renderGroupedSection(subCases, activeSub)
        )
      ) : (
        // Board level — sub-category KPI cards + standalone (ungrouped) cases.
        <div className="space-y-6">
          {subcategories.length > 0 && (
            <div>
              <h2 className="mb-2 text-sm font-semibold text-slate-700">Categories</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {subcategories.map((sub) => {
                  const st = subStats(sub.subcategorykey);
                  return (
                    <div
                      key={sub.subcategorykey}
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveSub(sub.subcategorykey)}
                      onKeyDown={(e) =>
                        (e.key === "Enter" || e.key === " ") && setActiveSub(sub.subcategorykey)
                      }
                      className="group flex cursor-pointer flex-col rounded-xl border border-slate-200 bg-card p-4 shadow-sm transition hover:border-brand-400 hover:shadow"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                            <Icons.Boards className="h-4 w-4" />
                          </span>
                          <span className="truncate font-semibold text-slate-900" title={sub.name}>
                            {sub.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSubcategory(sub.subcategorykey);
                          }}
                          aria-label="Delete category"
                          title="Delete category"
                          className="flex-none rounded-md p-1 text-slate-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-500 group-hover:opacity-100"
                        >
                          <Icons.Trash className="h-4 w-4" />
                        </button>
                      </div>
                      {sub.description && (
                        <p className="mt-1 line-clamp-2 text-xs text-slate-500">{sub.description}</p>
                      )}
                      <div className="mt-3 flex items-end justify-between">
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-semibold text-slate-900">{st.total}</span>
                          <span className="text-xs text-slate-400">
                            test case{st.total === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] font-medium">
                          <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                            {st.passed} pass
                          </span>
                          <span className="rounded-full bg-rose-50 px-1.5 py-0.5 text-rose-700 ring-1 ring-inset ring-rose-600/20">
                            {st.failed} fail
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Test cases directly under the board (standalone). */}
          {cases.length === 0 && subcategories.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
              No test cases yet. Create your first one, or add a category to organise them.
            </div>
          ) : standaloneCases.length > 0 ? (
            <div>
              {subcategories.length > 0 && (
                <h2 className="mb-2 text-sm font-semibold text-slate-700">Test cases</h2>
              )}
              {renderGroupedSection(standaloneCases, null)}
            </div>
          ) : caseQuery && subcategories.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
              No test cases match <span className="font-medium text-slate-700">"{caseQuery}"</span>.
            </div>
          ) : null}
        </div>
      )}

      {propsFor && <PropertiesModal testCase={propsFor} onClose={() => setPropsFor(null)} />}
      {deleteTarget && (
        <ConfirmModal
          title="Delete test case"
          message={
            deleteTarget.name
              ? `Delete the test case "${deleteTarget.name}"? This cannot be undone.`
              : "Delete this test case? This cannot be undone."
          }
          confirmLabel="Delete"
          busy={deleteBusy}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDeleteCase}
        />
      )}
      {/* The questions handlers ask before they act (useConfirm). */}
      {confirmDialog}
      {groupDeleteTarget && (
        <ConfirmModal
          title="Delete sub-category"
          message={
            groupDeleteTarget.name
              ? `Delete the empty sub-category "${groupDeleteTarget.name}"? This cannot be undone.`
              : "Delete this empty sub-category? This cannot be undone."
          }
          confirmLabel="Delete"
          busy={groupDeleteBusy}
          onCancel={() => setGroupDeleteTarget(null)}
          onConfirm={confirmDeleteGroup}
        />
      )}
      {historyFor && <RunHistory testCase={historyFor} onClose={() => setHistoryFor(null)} />}
      {addSubOpen && (
        <AddSubcategoryModal onClose={() => setAddSubOpen(false)} onSubmit={addSubcategory} />
      )}
      {groupModal?.mode === "create" && (
        <GroupNameModal
          title="Add to a sub-category"
          submitLabel="Create sub-category"
          onClose={() => setGroupModal(null)}
          onSubmit={createGroupFromSelection}
        />
      )}
      {groupModal?.mode === "rename" && (
        <GroupNameModal
          title="Rename sub-category"
          submitLabel="Save"
          initialName={groupModal.name}
          onClose={() => setGroupModal(null)}
          onSubmit={(name) => renameGroup(groupModal.groupkey, name)}
        />
      )}
      {/* Runner prompts surfaced at the board level so they appear no matter
          which page the user is on (board list, group view, etc.) — not only
          inside the full-page run viewer. Shows the first pending request. */}
      {(() => {
        const credId = Object.keys(pendingCredentials).find((id) => pendingCredentials[id]);
        if (!credId) return null;
        return (
          <CredentialsPromptModal onSubmit={(u, p) => submitCredentials(credId, u, p)} />
        );
      })()}
      {(() => {
        const promptId = Object.keys(pendingPrompts).find((id) => pendingPrompts[id]);
        const p = promptId ? pendingPrompts[promptId] : null;
        if (!p) return null;
        return (
          <DynamicPromptModal
            prompt={p}
            onSubmit={(value) => submitPromptInput(promptId, p.index, value)}
          />
        );
      })()}

      {toast && (
        <div
          role="status"
          className={`fixed top-6 right-6 z-[60] flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ring-1 ${
            toast.kind === "error"
              ? "bg-rose-600 ring-rose-500"
              : toast.kind === "info"
              ? "bg-slate-700 ring-slate-600"
              : "bg-emerald-600 ring-emerald-500"
          }`}
        >
          {toast.kind === "error" ? (
            <Icons.X className="h-4 w-4" />
          ) : toast.kind === "info" ? (
            <Icons.Minus className="h-4 w-4" />
          ) : (
            <Icons.Check className="h-4 w-4" />
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}


